import classNames from "classnames";
import React, { useState } from "react";
import KuikaSwap, { KuikaSwapDirection } from "../kuika-swap/kuika-swap";
import "./kuika-number.scss";

export interface IKuikaNumberProps {
  value: number;
  /**
   * Turns the value into the text on screen. Defaults to `Intl.NumberFormat("en-US")`, byte-identical
   * to `value.toLocaleString("en-US")` and the same on every machine locale. Pass one for a cap such as
   * `(v) => (v > 9 ? "9+" : String(v))`.
   */
  format?: (value: number) => string;
  className?: string;
  "data-testid"?: string;
}

/** The last change of `value`: which way it went and how many slots the text had before it. */
interface INumberMove {
  value: number;
  length: number;
  direction: KuikaSwapDirection;
  /** Slots at or past this position (counted from the right) are new with this change. */
  enteredFrom: number | null;
}

const EN_US_NUMBER: Intl.NumberFormat = new Intl.NumberFormat("en-US");

const formatEnUs = (value: number): string => EN_US_NUMBER.format(value);

/**
 * A number whose changed digits roll instead of cutting: `1,299 → 1,300` rolls the last three
 * slots and leaves `1,` still. Up rolls up, down rolls down.
 *
 * - Slots pair from the right, so the ones digit always meets the ones digit and a thousands
 *   separator keeps its place when the value gains or loses a digit. A slot that appears on the
 *   left rolls in; one that goes away leaves at once.
 * - The first render never animates.
 * - The value is in the DOM as text exactly once: the visually hidden copy, which assistive tech
 *   reads. The rolling slots are `aria-hidden` and draw their characters from `data-char` through
 *   CSS, so they carry no text — `textContent` reads "3", not "33", and never a leaving digit.
 *   The root carries the raw number as `data-value`. Announcing a change (`aria-live`) is the
 *   owner's call.
 * - Reduced motion needs nothing here: KuikaSwap's keyframes turn into a crossfade.
 */
const KuikaNumber = ({
  value,
  format = formatEnUs,
  className,
  "data-testid": dataTestId
}: IKuikaNumberProps): JSX.Element => {
  const text: string = format(value);
  const characters: string[] = Array.from(text);

  // Derived from the previous value during render (React's documented pattern). The move lasts until
  // the next change, so a re-render with the same value neither flips the direction (which would
  // restart a leaver mid-exit) nor ends a slot's entrance early.
  const [move, setMove] = useState<INumberMove>({
    value,
    length: characters.length,
    direction: "up",
    enteredFrom: null
  });
  if (value !== move.value) {
    setMove({
      value,
      length: characters.length,
      direction: value > move.value ? "up" : "down",
      enteredFrom: move.length
    });
  }
  const direction: KuikaSwapDirection = move.direction;

  return (
    <span className={classNames("kuika-number", className)} data-testid={dataTestId} data-value={String(value)}>
      <span className={classNames("kuika-number__slots", `kuika-number__slots--${direction}`)} aria-hidden="true">
        {characters.map((character: string, index: number): JSX.Element => {
          const position: number = characters.length - 1 - index;
          const isNewSlot: boolean = move.enteredFrom !== null && position >= move.enteredFrom;
          return (
            <KuikaSwap
              key={position}
              contentKey={character}
              variant="roll"
              direction={direction}
              className={classNames("kuika-number__slot", { "kuika-number__slot--enter": isNewSlot })}
            >
              <span className="kuika-number__char" data-char={character} />
            </KuikaSwap>
          );
        })}
      </span>
      <span className="kuika-visually-hidden">{text}</span>
    </span>
  );
};

export default KuikaNumber;
